import React,{useState,useMemo,useEffect,useCallback,useRef}from"react";
import{CheckCircle2,LayoutDashboard,Sun,Layers,Users,Inbox,UserCheck,Settings,Plus,LogOut,RefreshCw,Wifi,WifiOff,AlertCircle,Radio,Eye,X,Bug,Search,HelpCircle}from"lucide-react";
import{InteractionRequiredAuthError}from"@azure/msal-browser";
import{BLUE,GOLD,DARK,SCOPES,msalInstance,makeMockTasks,dispName,nextRecurDate}from"./config";
import * as Graph from"./Graph";
import Login from"./components/Login";
import{Detail}from"./components/TaskCard";
import TaskModal from"./components/TaskModal";
import FeedbackModal from"./components/FeedbackModal";
import SearchModal from"./components/SearchModal";
import HelpModal from"./components/HelpModal";
import{Dashboard,MyDay,WorkQueue,CompanyTasks,InboxView,TeamView}from"./components/Views";
import AdminPage from"./components/AdminPage";
import{StandupScreen}from"./components/StandupScreen";
import TeamsConfig from"./components/TeamsConfig";
import{initTeams}from"./teams";
import{ProductTrack}from"./components/IssueTrack";

function Spin(){return<div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-200 border-t-blue-600"/>;}

export default function App(){
  const[cu,setCu]=useState(null);
  const[tasks,setTasks]=useState([]);
  const[allUsers,setAllUsers]=useState([]);
  const[projects,setProjects]=useState([]);
  const[queue,setQueue]=useState(()=>{try{return JSON.parse(localStorage.getItem("dac_wq_order")||"null")||[];}catch{return[];}});
  const[view,setView]=useState(()=>{
    const p=new URLSearchParams(window.location.search).get("view");
    return(p&&["dashboard","myday","workqueue","company","inbox","team"].includes(p))?p:"dashboard";
  });
  const[sel,setSel]=useState(null);
  const[issues,setIssues]=useState([]);
  const[loadingIssues,setLoadingIssues]=useState(false);
  const[issueSel,setIssueSel]=useState(null);
  const[showAdd,setShowAdd]=useState(false);
  const[showStandup,setShowStandup]=useState(false);
  const[standupTasks,setStandupTasks]=useState([]);
  const[showFeedback,setShowFeedback]=useState(false);
  const[showSearch,setShowSearch]=useState(false);
  const[showHelp,setShowHelp]=useState(false);
  const[loading,setLoading]=useState(false);
  const[syncError,setSyncError]=useState(null);
  const[realCu,setRealCu]=useState(null); // set when impersonating another user
  const lastSyncRef=useRef(0);

  // Initialize MSAL and handle redirect response
  useEffect(()=>{
    // Start Teams detection in parallel with MSAL init (doesn't block)
    initTeams();

    msalInstance.initialize().then(()=>{
      // Handle Teams popup login result
      const onTeamsLogin=async(e)=>{
        const response=e.detail;
        if(!response)return;
        try{
          const tok=response.accessToken;
          const rawEmail=(response.account.username||"").toLowerCase();
          const email=rawEmail.split("@")[0].replace(/_/g,".")+"@dacrobotics.com";
          const[me,userRec]=await Promise.all([
            Graph.getMe(tok),
            Graph.getUserRecord(tok,email).catch(()=>null)
          ]);
          const finalEmail=(me.mail||me.userPrincipalName||rawEmail).toLowerCase().split("@")[0].replace(/_/g,".")+"@dacrobotics.com";
          let rec=userRec;
          if(!rec){
            try{rec=await Graph.upsertUser(tok,{email:finalEmail,displayName:me.displayName,role:"User",manager1:"",manager2:"",isActive:true,department:"",jobTitle:""});}
            catch(e){console.warn("Could not provision user",e);}
          }
          login({email:finalEmail,name:me.displayName,token:tok,account:response.account,mode:"live",role:rec?.role||"User",productTrackRole:rec?.productTrackRole||"none",userRec:rec});
        }catch(e){console.error("Teams login failed:",e);}
      };
      window.addEventListener("teams-login",onTeamsLogin);

      msalInstance.handleRedirectPromise().then(async(response)=>{
        if(response){
          try{
            // response.accessToken is already valid — no need for acquireTokenSilent here
            const tok=response.accessToken;
            // Fire getMe and getUserRecord in parallel using the email from idTokenClaims
            const rawEmail=(response.account.username||"").toLowerCase();
            const email=rawEmail.split("@")[0].replace(/_/g,".")+"@dacrobotics.com";
            const[me,userRec]=await Promise.all([
              Graph.getMe(tok),
              Graph.getUserRecord(tok,email).catch(()=>null)
            ]);
            const finalEmail=(me.mail||me.userPrincipalName||rawEmail).toLowerCase().split("@")[0].replace(/_/g,".")+"@dacrobotics.com";
            let rec=userRec;
            if(!rec){
              try{rec=await Graph.upsertUser(tok,{email:finalEmail,displayName:me.displayName,role:"User",manager1:"",manager2:"",isActive:true,department:"",jobTitle:""});}
              catch(e){console.warn("Could not provision user",e);}
            }
            login({email:finalEmail,name:me.displayName,token:tok,account:response.account,mode:"live",role:rec?.role||"User",productTrackRole:rec?.productTrackRole||"none",userRec:rec});
          }catch(e){console.error("Token acquisition failed:",e);}
        }
      }).catch(console.error);
      return()=>window.removeEventListener("teams-login",onTeamsLogin);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>{
    const h=e=>{if((e.ctrlKey||e.metaKey)&&e.key==="k"){e.preventDefault();setShowSearch(s=>!s);}};
    document.addEventListener("keydown",h);
    return()=>document.removeEventListener("keydown",h);
  },[]);

  const getToken=useCallback(async()=>{
    if(!cu?.account)return cu?.token;
    try{
      const r=await msalInstance.acquireTokenSilent({scopes:SCOPES,account:cu.account});
      return r.accessToken;
    }catch(err){
      if(err instanceof InteractionRequiredAuthError){const r=await msalInstance.acquireTokenPopup({scopes:SCOPES});return r.accessToken;}
      throw err;
    }
  },[cu]);

  const loadTasks=useCallback(async(user,silent=false)=>{
    if(!user.token)return;
    if(!silent){setLoading(true);setSyncError(null);}
    try{
      const tok=await msalInstance.acquireTokenSilent({scopes:SCOPES,account:user.account}).then(r=>r.accessToken).catch(()=>user.token);
      const[fetched,company,users,projs]=await Promise.all([Graph.getTasks(tok,user.email),Graph.getCompanyTasks(tok).catch(()=>[]),Graph.getUsers(tok).catch(()=>[]),Graph.getProjects().catch(()=>[])]);
      const allFetched=[...fetched,...company];
      setTasks(allFetched);
      setAllUsers(users);
      setProjects(projs);
      setQueue(prev=>{
        const me=user.email.toLowerCase();
        // Build the set of valid task IDs for this user's queue
        const validIds=new Set(allFetched.filter(t=>{
          if(["completed","declined"].includes(t.sta))return false;
          // Exclude tasks I own but assigned to someone else
          if(t.own.toLowerCase()===me&&t.asgn&&t.asgn.toLowerCase()!==me)return false;
          return t.asgn.toLowerCase()===me||t.own.toLowerCase()===me;
        }).map(t=>t.id));
        // Keep existing drag order — only drop tasks no longer valid
        const kept=prev.filter(id=>validIds.has(id));
        // Append any new tasks not yet in the queue
        const keptSet=new Set(kept);
        const added=[...validIds].filter(id=>!keptSet.has(id));
        return [...kept,...added];
      });
      lastSyncRef.current=Date.now();
    }catch(err){if(!silent)setSyncError("Could not sync: "+err.message);}
    finally{if(!silent)setLoading(false);}
  },[]);

  // Persist queue order to localStorage so drag order survives page refreshes and background syncs
  useEffect(()=>{if(queue.length)localStorage.setItem("dac_wq_order",JSON.stringify(queue));},[queue]);

  // Silent background refresh every 2 minutes so assigned tasks appear without manual sync
  useEffect(()=>{
    if(!cu)return;
    const id=setInterval(()=>loadTasks(cu,true),2*60*1000);
    return()=>clearInterval(id);
  },[cu,loadTasks]);

  const fetchIssues=useCallback(async()=>{
    setLoadingIssues(true);
    try{const tok=await getToken();const data=await Graph.getIssues(tok);setIssues(data);}
    catch(e){console.error("Failed to load issues:",e);}
    finally{setLoadingIssues(false);}
  },[getToken]);

  const createIssue=useCallback(async(data)=>{
    const tok=await getToken();
    const issue=await Graph.createIssue({...data,reporterEmail:data.reporterEmail||cu.email,createdDate:new Date().toISOString().slice(0,10),priorityRank:issues.length+1});
    setIssues(prev=>[...prev,issue]);
  },[getToken,cu,issues.length]);

  const editIssue=useCallback(async(issue)=>{
    await Graph.updateIssue(issue);
    setIssues(prev=>prev.map(i=>i.id===issue.id?issue:i));
  },[]);

  const deleteIssue=useCallback(async(issue)=>{
    await Graph.deleteIssue(issue);
    setIssues(prev=>prev.filter(i=>i.id!==issue.id));
  },[]);

  const login=user=>{
    if(user.mode==="demo"){const m=makeMockTasks(user.email);setTasks(m);setQueue(m.map(t=>t.id));}
    else loadTasks(user);
    setCu(user);
  };
  const logout=()=>{setCu(null);setRealCu(null);setTasks([]);setQueue([]);setAllUsers([]);setProjects([]);setView("dashboard");setSel(null);localStorage.removeItem("dac_wq_order");};

  // Impersonate another user — keeps the real MSAL token/account so SP writes still work
  const impersonate=useCallback((user)=>{
    const target={...cu,email:user.email,name:user.displayName,role:user.role};
    setRealCu(cu);
    setCu(target);
    setSel(null);setView("dashboard");
    loadTasks(target);
  },[cu,loadTasks]);

  const stopImpersonating=useCallback(()=>{
    if(!realCu)return;
    setCu(realCu);
    setRealCu(null);
    setSel(null);setView("dashboard");
    loadTasks(realCu);
  },[realCu,loadTasks]);

  const allVis=useMemo(()=>!cu?[]:tasks.filter(t=>t.vis==="company"||t.own.toLowerCase()===cu.email.toLowerCase()||t.asgn.toLowerCase()===cu.email.toLowerCase()||t.reqBy?.toLowerCase()===cu.email.toLowerCase()),[tasks,cu]);
  const mine=useMemo(()=>allVis.filter(t=>t.asgn.toLowerCase()===cu?.email.toLowerCase()||t.own.toLowerCase()===cu?.email.toLowerCase()),[allVis,cu]);
  const inboxPending=useMemo(()=>tasks.filter(t=>t.asgn.toLowerCase()===cu?.email.toLowerCase()&&t.acc==="pending"),[tasks,cu]);
  const inboxNotifs=useMemo(()=>tasks.filter(t=>t.own.toLowerCase()===cu?.email.toLowerCase()&&t.asgn&&t.asgn!==t.own&&(t.acc==="accepted"||t.acc==="declined")),[tasks,cu]);
  const inboxCount=inboxPending.length;

  // Helper to build a base history entry for a task
  const hist=(task,action,extra={})=>({
    taskId:task.id,taskTitle:task.title,listType:task.listType||"tasks",
    action,changedBy:cu.email,...extra,
    title:`${cu.email.split("@")[0]} ${action.replace(/_/g," ")} "${task.title}"`
  });

  // Detect changed fields between old and new task, log one entry per changed field
  const logEdits=(oldTask,newTask)=>{
    const WATCHED=[
      {k:"sta",f:"status_changed",lb:"Status"},
      {k:"pri",f:"priority_changed",lb:"Priority"},
      {k:"dueDate",f:"due_date_changed",lb:"Due Date"},
      {k:"title",f:"title_changed",lb:"Title"},
      {k:"desc",f:"desc_changed",lb:"Description"},
      {k:"asgn",f:"assigned",lb:"Assignee"},
      {k:"vis",f:"visibility_changed",lb:"Visibility"},
      {k:"project",f:"project_changed",lb:"Project"},
      {k:"notes",f:"notes_changed",lb:"Notes"},
      {k:"blockReason",f:"block_reason_changed",lb:"Block Reason"},
    ];
    WATCHED.forEach(({k,f,lb})=>{
      const ov=String(oldTask[k]||""),nv=String(newTask[k]||"");
      if(ov!==nv) Graph.logHistory(hist(newTask,f,{fieldName:lb,oldValue:ov,newValue:nv,details:`${lb}: "${ov}" → "${nv}"`}));
    });
    // Recurrence
    const or=JSON.stringify(oldTask.recurrence||null),nr=JSON.stringify(newTask.recurrence||null);
    if(or!==nr) Graph.logHistory(hist(newTask,"recurrence_changed",{fieldName:"Recurrence",oldValue:or,newValue:nr}));
  };

  const accept=async id=>{
    const task=tasks.find(t=>t.id===id);if(!task)return;
    const upd={...task,sta:"todo",acc:"accepted"};
    if(cu.token&&task.spId){
      try{
        const tok=await getToken();
        await Graph.updateTask(tok,upd);
        Graph.logHistory(hist(task,"accepted",{details:`Accepted by ${cu.email}`}));
      }catch(e){
        console.error(e);
        setSyncError("Could not save acceptance — check SharePoint permissions.");
        return;
      }
    }
    setTasks(ts=>ts.map(t=>t.id===id?upd:t));
    if(!queue.includes(id))setQueue(q=>[...q,id]);
  };

  const decline=async id=>{
    const task=tasks.find(t=>t.id===id);if(!task)return;
    const upd={...task,sta:"declined",acc:"declined"};
    if(cu.token&&task.spId){
      try{
        const tok=await getToken();
        await Graph.updateTask(tok,upd);
        Graph.logHistory(hist(task,"declined",{details:`Declined by ${cu.email}`}));
      }catch(e){
        console.error(e);
        setSyncError("Could not save decline — check SharePoint permissions.");
        return;
      }
    }
    setTasks(ts=>ts.map(t=>t.id===id?upd:t));
    setQueue(q=>q.filter(x=>x!==id));
  };

  const sendBack=async id=>{
    const task=tasks.find(t=>t.id===id);if(!task)return;
    const upd={...task,sta:"pending",acc:"pending"};
    try{
      const tok=await getToken();
      await Graph.updateTask(tok,upd);
      Graph.logHistory(hist(task,"assigned",{details:`Re-sent to ${task.asgn} by ${cu.email}`}));
      const assigneeName=dispName(task.asgn,allUsers);
      const ownerName=dispName(cu.email,allUsers)||cu.name;
      Graph.sendAssignmentEmail(upd,assigneeName,ownerName);
    }catch(e){setSyncError("Could not send back: "+e.message);return;}
    setTasks(ts=>ts.map(t=>t.id===id?upd:t));
  };

  const completeOccurrence=async id=>{
    const task=tasks.find(t=>t.id===id);if(!task||!task.recurrence?.enabled)return;
    const nextDue=nextRecurDate(task.recurrence,task.dueDate);
    const upd={...task,sta:"todo",dueDate:nextDue};
    try{
      const tok=await getToken();
      await Graph.updateTask(tok,upd);
      Graph.logHistory(hist(task,"status_changed",{fieldName:"Status",details:`Recurring occurrence completed by ${cu.email} — next due ${nextDue}`}));
    }catch(e){setSyncError("Could not save: "+e.message);return;}
    setTasks(ts=>ts.map(t=>t.id===id?upd:t));
  };

  const toggle=async id=>{
    const task=tasks.find(x=>x.id===id);if(!task)return;
    const isDone=["completed","review"].includes(task.sta);
    let newSta;
    if(isDone){
      newSta=task.sta==="review"?"in_progress":"todo";
    } else {
      // Route to "review" for assigned or company tasks unless the current user is a manager/admin completing their own task
      const isAssignedToOther=task.asgn&&task.asgn.toLowerCase()!==task.own.toLowerCase();
      const isCompanyTask=task.listType==="company"||task.vis==="company";
      const cuIsOwner=task.own.toLowerCase()===cu.email.toLowerCase();
      const cuIsManagerOrAdmin=cu.role==="Manager"||cu.role==="Admin";
      const canSelfComplete=cuIsOwner&&cuIsManagerOrAdmin;
      newSta=(isAssignedToOther||isCompanyTask)&&!canSelfComplete?"review":"completed";
    }
    const todayStr=new Date().toISOString().slice(0,10);
    const upd={...task,sta:newSta,
      completedDate:newSta==="completed"?todayStr:newSta==="todo"||newSta==="in_progress"?"":task.completedDate
    };
    setTasks(ts=>ts.map(x=>x.id===id?upd:x));
    if(newSta==="completed")setQueue(q=>q.filter(x=>x!==id));
    if(cu.token&&task.spId){
      try{const tok=await getToken();await Graph.updateTask(tok,upd);}catch(e){console.error(e);}
      Graph.logHistory(hist(task,"status_changed",{fieldName:"Status",oldValue:task.sta,newValue:upd.sta,details:`Status: "${task.sta}" → "${upd.sta}"`}));
    }
  };

  const approveTask=async id=>{
    const task=tasks.find(x=>x.id===id);if(!task)return;
    const todayStr=new Date().toISOString().slice(0,10);
    const upd={...task,sta:"completed",completedDate:todayStr};
    setTasks(ts=>ts.map(x=>x.id===id?upd:x));
    setQueue(q=>q.filter(x=>x!==id));
    if(cu.token&&task.spId){
      try{const tok=await getToken();await Graph.updateTask(tok,upd);}catch(e){console.error(e);}
      Graph.logHistory(hist(task,"status_changed",{fieldName:"Status",oldValue:task.sta,newValue:"completed",details:`Approved as completed by ${cu.email}`}));
    }
  };

  const reopenTask=async id=>{
    const task=tasks.find(x=>x.id===id);if(!task)return;
    const upd={...task,sta:"in_progress"};
    setTasks(ts=>ts.map(x=>x.id===id?upd:x));
    if(cu.token&&task.spId){
      try{const tok=await getToken();await Graph.updateTask(tok,upd);}catch(e){console.error(e);}
      Graph.logHistory(hist(task,"status_changed",{fieldName:"Status",oldValue:task.sta,newValue:"in_progress",details:`Reopened by ${cu.email}`}));
    }
  };

  const add=async t=>{
    let saved=t;
    if(cu.token){try{const tok=await getToken();saved=await Graph.createTask(tok,t);}catch(e){console.error(e);}}
    setTasks(ts=>[saved,...ts]);
    if((saved.asgn.toLowerCase()===cu.email.toLowerCase()||saved.own.toLowerCase()===cu.email.toLowerCase())&&saved.acc!=="pending"){setQueue(q=>[saved.id,...q]);}
    Graph.logHistory(hist(saved,"created",{details:`Created by ${cu.email}${saved.asgn&&saved.asgn!==cu.email?`, assigned to ${saved.asgn}`:""}`}));
    if(saved.asgn&&saved.asgn.toLowerCase()!==cu.email.toLowerCase()){
      const assigneeName=dispName(saved.asgn,allUsers);
      const ownerName=dispName(cu.email,allUsers)||cu.name;
      Graph.sendAssignmentEmail(saved,assigneeName,ownerName).catch(e=>console.warn("Assignment email failed:",e));
    }
    setSel(saved.id);
    setView(saved.vis==="company"?"company":"myday");
  };

  const editTask=async t=>{
    let saved=t;
    const oldTask=tasks.find(x=>x.id===t.id);
    if(cu.token&&t.spId){
      try{
        const tok=await getToken();
        // Never migrate a company list task — always update in place
        const newListType=(t.vis==="company"||oldTask?.listType==="company")?"company":"tasks";
        if(oldTask&&oldTask.listType!==newListType){
          await Graph.deleteTask(tok,oldTask);
          const created=await Graph.createTask(tok,{...t,spId:null});
          saved={...created,id:t.id};
        } else {
          saved=await Graph.updateTask(tok,t);
        }
      }catch(e){console.error(e);}
    }
    if(oldTask) logEdits(oldTask,saved);
    setTasks(ts=>ts.map(x=>x.id===t.id?saved:x));
    if(["completed","declined"].includes(saved.sta))setQueue(q=>q.filter(x=>x!==t.id));
    // Send assignment email if assignee changed to a new person
    if(saved.asgn&&saved.asgn.toLowerCase()!==cu.email.toLowerCase()&&(!oldTask||oldTask.asgn.toLowerCase()!==saved.asgn.toLowerCase())){
      const assigneeName=dispName(saved.asgn,allUsers);
      const ownerName=dispName(cu.email,allUsers)||cu.name;
      Graph.sendAssignmentEmail(saved,assigneeName,ownerName).catch(e=>console.warn("Assignment email failed:",e));
    }
  };

  const requestUpdate=async(task,note="")=>{
    const target=task.asgn||task.own;
    if(!target||target.toLowerCase()===cu.email.toLowerCase())return;
    const requesterName=dispName(cu.email,allUsers)||cu.name;
    const targetName=dispName(target,allUsers);
    await Graph.sendUpdateRequestEmail({...task,asgn:target},requesterName,targetName,note);
    Graph.logHistory(hist(task,"update_requested",{details:`Update requested by ${cu.email}${note?`: "${note}"`:""}` }));
  };

  const deleteTask=async id=>{
    const task=tasks.find(x=>x.id===id);
    if(!task)return;
    Graph.logHistory(hist(task,"deleted",{details:`Deleted by ${cu.email}`}));
    if(cu.token&&task.spId){try{const tok=await getToken();await Graph.deleteTask(tok,task);}catch(e){console.error(e);}}
    setTasks(ts=>ts.filter(x=>x.id!==id));
    setQueue(q=>q.filter(x=>x!==id));
    setSel(null);
  };

  const standupToggle=async(task,val)=>{
    const upd={...task,standup:val};
    setTasks(ts=>ts.map(x=>x.id===task.id?upd:x));
    // Keep standupTasks in sync: add when enabling, remove when disabling
    setStandupTasks(st=>val
      ?(st.some(x=>x.id===upd.id)?st.map(x=>x.id===upd.id?upd:x):[...st,upd])
      :st.filter(x=>x.id!==task.id));
    if(cu.token&&task.spId){try{await Graph.updateTaskStandup(task,val);}catch(e){console.error(e);}}
    Graph.logHistory(hist(task,"standup_toggled",{fieldName:"Standup",oldValue:String(!val),newValue:String(val),details:`Standup ${val?"enabled":"disabled"} by ${cu.email}`}));
  };

  const standupUpdateTask=async(task,patch)=>{
    const updated={...task,...patch};
    setTasks(ts=>ts.map(x=>x.id===task.id?updated:x));
    setStandupTasks(st=>st.map(x=>x.id===task.id?updated:x));
    try{await Graph.updateTask(null,updated);}catch(e){console.error("standupUpdateTask",e);}
    logEdits(task,updated);
    const actor=(task.asgn&&task.acc==="accepted")?task.asgn:task.own;
    Graph.logHistory(hist(updated,"standup_edit",{details:`Standup quick-edit on behalf of ${actor}`}));
    return updated;
  };

  const standupSaveNote=async(task,note,newStatus)=>{
    try{
      const updated=await Graph.addStandupNote(task,note,newStatus);
      setTasks(ts=>ts.map(x=>x.id===task.id?updated:x));
      setStandupTasks(st=>st.map(x=>x.id===task.id?updated:x));
      const actor=(task.asgn&&task.acc==="accepted")?task.asgn:task.own;
      Graph.logHistory(hist(task,"standup_note",{details:`Standup note by ${actor}: "${note}"${newStatus?` → ${newStatus}`:""}`}));
      return updated;
    }catch(e){console.error(e);}
  };

  const switchView=useCallback((id)=>{
    setView(id);setSel(null);
    if(cu&&cu.token&&Date.now()-lastSyncRef.current>2*60*1000){loadTasks(cu,true);}
  },[cu,loadTasks]);

  // Load issues when navigating to ProductTrack
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(()=>{
    if(cu&&view==="producttrack"&&issues.length===0&&!loadingIssues) fetchIssues();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[view,cu]);

  // Teams channel tab config page — shown before auth
  if(new URLSearchParams(window.location.search).get("teamsConfig"))
    return<TeamsConfig projects={projects}/>;

  if(!cu)return<Login onLogin={login}/>;

  // isAdmin: always the real user so admin nav stays reachable while impersonating
  const isAdmin=(realCu||cu).role==="Admin";
  // isManager: reflects the currently-active role (impersonated or real)
  const isManager=cu.role==="Manager"||cu.role==="Admin";
  // ProductTrack access — TaskHub Admins get admin rights automatically
  const ptRole=isAdmin?"admin":(cu.productTrackRole||"none");
  const canAccessPT=ptRole!=="none";
  const selTask=tasks.find(t=>t.id===sel);

  const nav=[
    {id:"dashboard",label:"Dashboard",Icon:LayoutDashboard,badge:0},
    {id:"myday",label:"My Day",Icon:Sun,badge:mine.filter(t=>!["completed","pending","declined"].includes(t.sta)&&t.acc!=="pending").length},
    {id:"workqueue",label:"Work Queue",Icon:Layers,badge:queue.length},
    {id:"company",label:"Company",Icon:Users,badge:tasks.filter(t=>t.vis==="company"&&t.sta!=="completed").length},
    {id:"inbox",label:"Inbox",Icon:Inbox,badge:inboxCount,highlight:inboxCount>0},
    ...(canAccessPT?[{id:"producttrack",label:"ProductTrack",Icon:Bug,badge:issues.filter(i=>["new","triaged","in_progress","testing"].includes(i.status)&&i.severity==="critical").length,ptOnly:true}]:[]),
    ...(isManager?[{id:"team",label:"My Team",Icon:UserCheck,badge:0}]:[]),
    ...(isManager?[{id:"admin",label:isAdmin?"Admin":"Manager Admin",Icon:Settings,badge:0,adminOnly:true}]:[]),
  ];

  const viewTitle={dashboard:"Dashboard",myday:"My Day",workqueue:"Work Queue",company:"Company Tasks",inbox:"Inbox",team:"My Team",admin:isAdmin?"Admin":"Manager Admin",producttrack:"ProductTrack"};

  return(
    <div className="flex h-screen overflow-hidden" style={{fontFamily:"system-ui,sans-serif",background:"#F1F5F9",color:"#111827"}}>
      {/* Sidebar — hidden on mobile */}
      <aside className="hidden md:flex w-52 flex-col shrink-0" style={{background:DARK}}>
        <div className="px-4 py-3 border-b" style={{borderColor:"#1E3560"}}>
          <img src="/dac-robotics-logo.png" alt="DAC Robotics" style={{height:"32px",width:"auto",borderRadius:"6px",background:"white",padding:"3px 6px",display:"block"}}/>
          <p className="text-xs mt-1.5" style={{color:"#4A6FA5"}}>TaskHub</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map(({id,label,Icon,badge,highlight,adminOnly,ptOnly})=>(
            <button key={id} onClick={()=>switchView(id)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm" style={view===id?{background:adminOnly?"#2D1B69":ptOnly?"#1A3A1A":BLUE,color:"white",fontWeight:600}:{color:"#8BADD4"}}>
              <Icon size={15}/>{label}
              {badge>0&&<span className="ml-auto text-xs rounded-full px-1.5 py-0.5 font-bold" style={highlight?{background:GOLD,color:"#4A2800"}:ptOnly?{background:"#DC2626",color:"white"}:{background:"rgba(255,255,255,0.15)",color:"white"}}>{badge}</span>}
            </button>
          ))}
        </nav>
        <div className="px-3 pb-3 space-y-2">
          <button onClick={()=>setShowAdd(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90" style={{background:GOLD}}><Plus size={16}/>New Task</button>
          <button onClick={async()=>{setShowStandup(true);try{const st=await Graph.getStandupTasks();setStandupTasks(st);}catch(e){console.error("Standup load:",e);}}} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-semibold text-white hover:opacity-90" style={{background:BLUE}}><Radio size={15}/>Daily Standup</button>
          <button onClick={()=>setShowFeedback(true)} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs hover:bg-white/10" style={{color:"#8BADD4"}}><Bug size={13}/>Report Issue / Feedback</button>
          {cu.token&&<button onClick={()=>loadTasks(cu)} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs text-white/60 hover:text-white hover:bg-white/10"><RefreshCw size={12}/>Sync SharePoint</button>}
        </div>
        <div className="px-4 py-3 border-t" style={{borderColor:"#1E3560"}}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0" style={{background:BLUE}}>{cu.name[0].toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate text-white">{cu.name}</p>
              <p className="text-xs flex items-center gap-1" style={{color:"#4A6FA5"}}>
                {cu.token?<><Wifi size={9} className="text-emerald-400"/>Live</>:<><WifiOff size={9} className="text-amber-400"/>Demo</>}
                {cu.role&&cu.role!=="User"&&<span className="ml-1 text-xs font-bold" style={{color:GOLD}}>{cu.role}</span>}
              </p>
            </div>
          </div>
          <button onClick={()=>setShowHelp(true)} title="Help guide" className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs hover:bg-white/10" style={{color:"#8BADD4"}}><HelpCircle size={13}/>Help &amp; Guide</button>
          <button onClick={logout} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs hover:bg-white/10" style={{color:"#8BADD4"}}><LogOut size={13}/>Sign out</button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Mobile top bar — hidden on desktop */}
        <div className="md:hidden flex items-center justify-between px-4 py-2 shrink-0 bg-white border-b border-slate-200" style={{paddingTop:"max(8px,env(safe-area-inset-top))"}}>
          <div className="flex flex-col items-start">
            <img src="/dac-robotics-logo.png" alt="DAC Robotics" style={{height:"22px",width:"auto",borderRadius:"3px",background:"white",padding:"2px 4px"}}/>
            <span style={{fontSize:"9px",fontWeight:700,letterSpacing:"0.08em",color:"#4A6FA5",marginTop:"1px"}}>TASKHUB</span>
          </div>
          <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            {viewTitle[view]||view}
            {view==="inbox"&&inboxCount>0&&<span className="text-xs font-bold px-1.5 py-0.5 rounded-full text-white" style={{background:GOLD}}>{inboxCount}</span>}
          </span>
          <div className="flex items-center gap-1.5">
            {cu.token&&<button onClick={()=>loadTasks(cu,true)} title="Sync SharePoint" className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 border border-slate-200 bg-white">{loading?<Spin/>:<RefreshCw size={14}/>}</button>}
            <button onClick={()=>setShowFeedback(true)} title="Report Issue / Feedback" className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 border border-slate-200 bg-white"><Bug size={14}/></button>
            <button onClick={()=>setShowAdd(true)} title="New task" className="w-8 h-8 rounded-full flex items-center justify-center text-white shadow" style={{background:GOLD}}><Plus size={16}/></button>
          </div>
        </div>

        {/* Impersonation banner */}
        {realCu&&(
          <div className="shrink-0 flex items-center justify-between px-4 py-2 gap-3" style={{background:"#F59E0B"}}>
            <div className="flex items-center gap-2 min-w-0">
              <Eye size={14} className="text-amber-900 shrink-0"/>
              <p className="text-xs md:text-sm font-semibold text-amber-900 truncate">Acting as <strong>{cu.name}</strong> · {cu.role}</p>
            </div>
            <button onClick={stopImpersonating} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-900/20 hover:bg-amber-900/30 text-amber-900 rounded-lg font-semibold shrink-0">
              <X size={11}/>Exit
            </button>
          </div>
        )}

        {/* Content row */}
        <div className="flex-1 flex overflow-hidden">
          <div className={"flex flex-col overflow-hidden flex-1"+(selTask?" md:w-1/2 md:flex-none":"")}>
            {/* Desktop header — hidden on mobile */}
            <header className="hidden md:flex bg-white border-b border-slate-200 px-6 py-3.5 shrink-0 items-center justify-between">
              <div>
                <h1 className="text-base font-semibold flex items-center gap-2">
                  {viewTitle[view]||view}
                  {view==="inbox"&&inboxCount>0&&<span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{background:GOLD}}>{inboxCount} pending</span>}
                  {view==="admin"&&<span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{background:"#EDE9FE",color:"#6D28D9"}}>Admin Only</span>}
                </h1>
                <p className="text-xs text-slate-400">{cu.name} · {cu.email}</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={()=>setShowSearch(true)} title="Search tasks (Ctrl+K)" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><Search size={13}/>Search</button>
                {loading&&<div className="flex items-center gap-2 text-xs text-slate-400"><Spin/>Syncing</div>}
                {syncError&&<div className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={11}/>{syncError}</div>}
              </div>
            </header>
            <main className="flex-1 overflow-y-auto p-4 md:p-5 pb-24 md:pb-5">
              {view==="dashboard"&&<Dashboard allTasks={tasks} myTasks={mine} setSel={setSel} setView={switchView} mode={cu.mode} cu={cu} allUsers={allUsers} onAccept={accept} onDecline={decline} onApprove={approveTask} onReopen={reopenTask}/>}
              {view==="myday"&&<MyDay myTasks={mine} all={tasks} sel={sel} setSel={setSel} cu={cu} canStandup={isManager} onStandupToggle={standupToggle} queue={queue} setQueue={setQueue} projects={projects}/>}
              {view==="workqueue"&&<WorkQueue tasks={allVis} all={tasks} queue={queue} setQueue={setQueue} sel={sel} setSel={setSel} onAccept={accept} onDecline={decline} cu={cu} canStandup={isManager} onStandupToggle={standupToggle} projects={projects}/>}
              {view==="company"&&<CompanyTasks tasks={tasks} sel={sel} setSel={setSel} cu={cu} canStandup={isManager} onStandupToggle={standupToggle} projects={projects}/>}
              {view==="inbox"&&<InboxView inboxPending={inboxPending} inboxNotifs={inboxNotifs} sel={sel} setSel={setSel} onAccept={accept} onDecline={decline}/>}
              {view==="team"&&isManager&&<TeamView tasks={tasks} cu={cu} allUsers={allUsers} sel={sel} setSel={setSel} canStandup={isManager} onStandupToggle={standupToggle} projects={projects}/>}
              {view==="admin"&&isManager&&<AdminPage cu={realCu||cu} getToken={getToken} projects={projects} setProjects={setProjects} onImpersonate={impersonate} impersonating={!!realCu} impersonatingEmail={cu.email} isAdmin={isAdmin}/>}
              {view==="producttrack"&&canAccessPT&&<ProductTrack issues={issues} sel={issueSel} setSel={setIssueSel} cu={{...cu,productTrackRole:ptRole}} allUsers={allUsers} projects={projects} onCreateIssue={createIssue} onEditIssue={editIssue} onDeleteIssue={deleteIssue} loading={loadingIssues}/>}
            </main>
          </div>

          {/* Detail panel — full screen on mobile, half panel on desktop */}
          {selTask&&<div className="fixed inset-0 z-40 bg-white flex flex-col overflow-hidden md:static md:inset-auto md:z-auto md:w-1/2 md:border-l md:border-slate-200"><Detail task={selTask} all={tasks} onClose={()=>setSel(null)} onToggle={toggle} onAccept={accept} onDecline={decline} onApprove={approveTask} onReopen={reopenTask} onEdit={editTask} onDelete={deleteTask} onRequestUpdate={requestUpdate} onSendBack={sendBack} onCompleteOccurrence={completeOccurrence} cu={cu} allUsers={allUsers} projects={projects} canStandup={isManager} onStandupToggle={standupToggle}/></div>}
        </div>

        {/* Mobile bottom nav — hidden on desktop */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex items-center justify-around z-30" style={{paddingBottom:"env(safe-area-inset-bottom)"}}>
          {nav.map(({id,label,Icon,badge,highlight})=>(
            <button key={id} onClick={()=>{setView(id);setSel(null);}} className={"flex flex-col items-center gap-0.5 py-2 px-2 min-w-0 "+(view===id?"text-blue-600":"text-slate-400")}>
              <div className="relative">
                <Icon size={21}/>
                {badge>0&&<span className="absolute -top-1 -right-1.5 text-xs rounded-full px-1 font-bold leading-none min-w-[15px] h-[15px] flex items-center justify-center text-white" style={{background:highlight?GOLD:BLUE,fontSize:"9px"}}>{badge}</span>}
              </div>
              <span className="text-xs font-medium" style={{fontSize:"9px"}}>{label}</span>
            </button>
          ))}
          <button onClick={async()=>{setShowStandup(true);try{const st=await Graph.getStandupTasks();setStandupTasks(st);}catch(e){console.error("Standup load:",e);}}} className="flex flex-col items-center gap-0.5 py-2 px-2 text-slate-400">
            <Radio size={21}/><span className="text-xs font-medium" style={{fontSize:"9px"}}>Standup</span>
          </button>
        </nav>
      </div>

      {showAdd&&<TaskModal onClose={()=>setShowAdd(false)} onAdd={add} all={tasks} cu={cu} projects={projects} allUsers={allUsers}/>}
      {showStandup&&<StandupScreen allTasks={standupTasks} users={allUsers} cu={cu} onSaveNote={standupSaveNote} onStandupToggle={standupToggle} onClose={()=>setShowStandup(false)} onUpdateTask={standupUpdateTask} projects={projects}/>}
      {showFeedback&&<FeedbackModal cu={cu} getToken={getToken} onClose={()=>setShowFeedback(false)}/>}
      {showSearch&&<SearchModal tasks={allVis} onClose={()=>setShowSearch(false)} onSelect={id=>{setSel(id);setShowSearch(false);}}/>}
      {showHelp&&<HelpModal onClose={()=>setShowHelp(false)}/>}
    </div>
  );
}
